/**
 * Color Schemes
 *
 * Each scheme defines the full palette for a design.
 * All colors are validated for minimum contrast against their background.
 *
 * Roles:
 *   bg       — canvas background
 *   primary  — main text (titles, lineup names)
 *   secondary — secondary text (venue, subheadings)
 *   accent   — highlight color (dates, CTA, section headings)
 *   detail   — small body text (address, times, info)
 *   footer   — footer text (must be readable, not decorative)
 *   lineArt  — line art overlay strokes
 *   lineArt2 — secondary line art / accent overlay
 *   isDark   — flag for UI theming decisions
 *
 * Contrast rules (approximate WCAG AA):
 *   primary  ≥ 7:1 against bg
 *   secondary ≥ 5:1 against bg
 *   accent   ≥ 4.5:1 against bg
 *   detail   ≥ 4.5:1 against bg
 *   footer   ≥ 4.5:1 against bg
 */

export const SCHEMES = {
  // ── Core schemes ────────────────────────────────────────

  dark: {
    name: 'Dark',
    bg: '#0d0d0d',
    primary: '#ffffff',
    secondary: '#d0d0d0',
    accent: '#ff4444',
    detail: '#b0b0b0',
    footer: '#909090',
    lineArt: '#ffffff',
    lineArt2: '#ff4444',
    isDark: true,
  },
  light: {
    name: 'Light',
    bg: '#f5f0eb',
    primary: '#1a1a1a',
    secondary: '#333333',
    accent: '#cc0000',
    detail: '#555555',
    footer: '#666666',
    lineArt: '#2a2a2a',
    lineArt2: '#cc0000',
    isDark: false,
  },
  red: {
    name: 'Bold Red',
    bg: '#cc0000',
    primary: '#ffffff',
    secondary: '#ffe0e0',
    accent: '#1a1a1a',
    detail: '#ffcccc',
    footer: '#ffd0d0',
    lineArt: '#ffffff',
    lineArt2: '#ffaa00',
    isDark: true,
  },

  // ── Warm / Earth tones ──────────────────────────────────

  warm: {
    name: 'Warm',
    bg: '#2c1810',
    primary: '#f5deb3',
    secondary: '#d4a574',
    accent: '#e8a52e',
    detail: '#c09060',
    footer: '#b88850',
    lineArt: '#6a4830',
    lineArt2: '#e8a52e',
    isDark: true,
  },
  rust: {
    name: 'Rust',
    bg: '#1e1410',
    primary: '#e8c8a8',
    secondary: '#c0946a',
    accent: '#d4602a',
    detail: '#b08060',
    footer: '#a07050',
    lineArt: '#5a3828',
    lineArt2: '#d4602a',
    isDark: true,
  },
  copper: {
    name: 'Copper',
    bg: '#1a1210',
    primary: '#e8d0b8',
    secondary: '#c8a080',
    accent: '#b87333',
    detail: '#b09070',
    footer: '#a08060',
    lineArt: '#6a4828',
    lineArt2: '#b87333',
    isDark: true,
  },
  terra: {
    name: 'Terra',
    bg: '#1a1008',
    primary: '#e8d0a8',
    secondary: '#c0a070',
    accent: '#c05830',
    detail: '#b09060',
    footer: '#a08050',
    lineArt: '#5a3820',
    lineArt2: '#c05830',
    isDark: true,
  },
  ember: {
    name: 'Ember',
    bg: '#1a0e08',
    primary: '#ffd8a8',
    secondary: '#e0a870',
    accent: '#ff5722',
    detail: '#c08850',
    footer: '#b08040',
    lineArt: '#6a3818',
    lineArt2: '#ff5722',
    isDark: true,
  },

  // ── Cool tones ──────────────────────────────────────────

  midnight: {
    name: 'Midnight',
    bg: '#0a0a2e',
    primary: '#e0e0ff',
    secondary: '#b0b0e0',
    accent: '#6c63ff',
    detail: '#9090c0',
    footer: '#8080b0',
    lineArt: '#3a4268',
    lineArt2: '#6c63ff',
    isDark: true,
  },
  ocean: {
    name: 'Ocean',
    bg: '#0a1628',
    primary: '#b8d8f8',
    secondary: '#7ab0d8',
    accent: '#00a8e8',
    detail: '#70a0c0',
    footer: '#6090b0',
    lineArt: '#1a4a68',
    lineArt2: '#00a8e8',
    isDark: true,
  },
  ice: {
    name: 'Ice',
    bg: '#0a1420',
    primary: '#c8e8ff',
    secondary: '#88c0e8',
    accent: '#00e5ff',
    detail: '#70a8d0',
    footer: '#6098c0',
    lineArt: '#204868',
    lineArt2: '#00e5ff',
    isDark: true,
  },
  ink: {
    name: 'Ink',
    bg: '#0a0e18',
    primary: '#d0d8e8',
    secondary: '#90a0c0',
    accent: '#3a6ea5',
    detail: '#7890b0',
    footer: '#6880a0',
    lineArt: '#283848',
    lineArt2: '#4a80c0',
    isDark: true,
  },

  // ── Greens ──────────────────────────────────────────────

  forest: {
    name: 'Forest',
    bg: '#0d1f0d',
    primary: '#c8e6c8',
    secondary: '#8fbc8f',
    accent: '#4caf50',
    detail: '#7aaa7a',
    footer: '#6a9a6a',
    lineArt: '#2a5a2a',
    lineArt2: '#4caf50',
    isDark: true,
  },
  moss: {
    name: 'Moss',
    bg: '#0e1a0e',
    primary: '#c0d8b8',
    secondary: '#88a880',
    accent: '#558b2f',
    detail: '#78a068',
    footer: '#689858',
    lineArt: '#2a5020',
    lineArt2: '#7cb342',
    isDark: true,
  },

  // ── Purples / Reds ──────────────────────────────────────

  sunset: {
    name: 'Sunset',
    bg: '#1a0a1e',
    primary: '#ffd4a3',
    secondary: '#e6a070',
    accent: '#ff6b35',
    detail: '#c08858',
    footer: '#b07848',
    lineArt: '#5a3060',
    lineArt2: '#ff6b35',
    isDark: true,
  },
  cherry: {
    name: 'Cherry',
    bg: '#180a0e',
    primary: '#f8d0d8',
    secondary: '#d89aa8',
    accent: '#dc143c',
    detail: '#c08090',
    footer: '#b07080',
    lineArt: '#6a2838',
    lineArt2: '#dc143c',
    isDark: true,
  },
  wine: {
    name: 'Wine',
    bg: '#1a0a12',
    primary: '#f0d0d8',
    secondary: '#c89aa8',
    accent: '#8b2252',
    detail: '#b08090',
    footer: '#a07080',
    lineArt: '#602840',
    lineArt2: '#c03070',
    isDark: true,
  },
  plum: {
    name: 'Plum',
    bg: '#180a20',
    primary: '#e8c8f8',
    secondary: '#b888d0',
    accent: '#9c27b0',
    detail: '#a080b8',
    footer: '#9070a8',
    lineArt: '#503868',
    lineArt2: '#ba68c8',
    isDark: true,
  },
  dusk: {
    name: 'Dusk',
    bg: '#1a1020',
    primary: '#f0d0b8',
    secondary: '#c8a088',
    accent: '#e86830',
    detail: '#b08868',
    footer: '#a07858',
    lineArt: '#5a3848',
    lineArt2: '#e86830',
    isDark: true,
  },

  // ── Neutrals / Monochrome ───────────────────────────────

  smoke: {
    name: 'Smoke',
    bg: '#2a2a2a',
    primary: '#e8e8e8',
    secondary: '#b0b0b0',
    accent: '#e0e0e0',
    detail: '#999999',
    footer: '#909090',
    lineArt: '#555555',
    lineArt2: '#c0a060',
    isDark: true,
  },
  charcoal: {
    name: 'Charcoal',
    bg: '#1c1c22',
    primary: '#e8e8f0',
    secondary: '#a0a0b8',
    accent: '#4a90d9',
    detail: '#8888a8',
    footer: '#7878a0',
    lineArt: '#50506a',
    lineArt2: '#4a90d9',
    isDark: true,
  },
  slate: {
    name: 'Slate',
    bg: '#282e34',
    primary: '#d8e0e8',
    secondary: '#a0b0c0',
    accent: '#00b8a9',
    detail: '#8898a8',
    footer: '#7888a0',
    lineArt: '#506070',
    lineArt2: '#00b8a9',
    isDark: true,
  },
  steel: {
    name: 'Steel',
    bg: '#202830',
    primary: '#d0d8e0',
    secondary: '#98a8b8',
    accent: '#5c86ac',
    detail: '#7888a0',
    footer: '#688898',
    lineArt: '#485868',
    lineArt2: '#5c86ac',
    isDark: true,
  },
  void: {
    name: 'Void',
    bg: '#060608',
    primary: '#d0d0d8',
    secondary: '#a0a0a8',
    accent: '#7070c0',
    detail: '#8080a0',
    footer: '#707098',
    lineArt: '#d0d0e0',
    lineArt2: '#7070c0',
    isDark: true,
  },
  obsidian: {
    name: 'Obsidian',
    bg: '#050505',
    primary: '#f0e6c8',
    secondary: '#c8b48a',
    accent: '#d4a520',
    detail: '#a09060',
    footer: '#908050',
    lineArt: '#c8a840',
    lineArt2: '#7ab8e8',
    isDark: true,
  },
  noir: {
    name: 'Noir',
    bg: '#080808',
    primary: '#f0f0f0',
    secondary: '#c0c0c0',
    accent: '#ffffff',
    detail: '#a0a0a0',
    footer: '#909090',
    lineArt: '#e8e8e8',
    lineArt2: '#e8c060',
    isDark: true,
  },
  neon: {
    name: 'Neon',
    bg: '#0a0a0a',
    primary: '#00ff88',
    secondary: '#00cc6a',
    accent: '#ff00ff',
    detail: '#00bb66',
    footer: '#00aa55',
    lineArt: '#00aa55',
    lineArt2: '#ff00ff',
    isDark: true,
  },
  solar: {
    name: 'Solar',
    bg: '#0e0e10',
    primary: '#f8f0c0',
    secondary: '#c8c090',
    accent: '#ffd600',
    detail: '#a0a060',
    footer: '#909050',
    lineArt: '#c8c020',
    lineArt2: '#ff6a00',
    isDark: true,
  },

  // ── Light schemes ───────────────────────────────────────

  blush: {
    name: 'Blush',
    bg: '#fef0f0',
    primary: '#3a1818',
    secondary: '#5a2828',
    accent: '#d4626a',
    detail: '#6a3838',
    footer: '#7a4848',
    lineArt: '#c09090',
    lineArt2: '#d4626a',
    isDark: false,
  },
  cream: {
    name: 'Cream',
    bg: '#faf6f0',
    primary: '#2c2416',
    secondary: '#4a3a28',
    accent: '#8b6914',
    detail: '#5a4a30',
    footer: '#6a5a40',
    lineArt: '#b0a080',
    lineArt2: '#8b6914',
    isDark: false,
  },
  arctic: {
    name: 'Arctic',
    bg: '#f0f6fa',
    primary: '#1a2a3a',
    secondary: '#3a4a5a',
    accent: '#2196f3',
    detail: '#4a5a6a',
    footer: '#5a6a7a',
    lineArt: '#8ab0cc',
    lineArt2: '#2196f3',
    isDark: false,
  },
  lavender: {
    name: 'Lavender',
    bg: '#f3eef8',
    primary: '#2a1a3a',
    secondary: '#4a3060',
    accent: '#7c4dff',
    detail: '#5a4070',
    footer: '#6a5080',
    lineArt: '#a090b8',
    lineArt2: '#7c4dff',
    isDark: false,
  },
  sage: {
    name: 'Sage',
    bg: '#eef2ec',
    primary: '#1a2818',
    secondary: '#3a4a38',
    accent: '#5a8a50',
    detail: '#4a5a48',
    footer: '#5a6a58',
    lineArt: '#8aaa80',
    lineArt2: '#5a8a50',
    isDark: false,
  },
  mint: {
    name: 'Mint',
    bg: '#eef8f4',
    primary: '#0a2a20',
    secondary: '#1a4a38',
    accent: '#00c896',
    detail: '#2a5a48',
    footer: '#3a6a58',
    lineArt: '#70c0a0',
    lineArt2: '#00c896',
    isDark: false,
  },
  pearl: {
    name: 'Pearl',
    bg: '#f8f6f4',
    primary: '#2a2828',
    secondary: '#4a4848',
    accent: '#706868',
    detail: '#585050',
    footer: '#686060',
    lineArt: '#b0a8a8',
    lineArt2: '#c07860',
    isDark: false,
  },
  sand: {
    name: 'Sand',
    bg: '#f5efe6',
    primary: '#2a2018',
    secondary: '#4a3a28',
    accent: '#c8a050',
    detail: '#5a4a30',
    footer: '#6a5a40',
    lineArt: '#b8a880',
    lineArt2: '#c8a050',
    isDark: false,
  },
  bone: {
    name: 'Bone',
    bg: '#f6f0e8',
    primary: '#2c2418',
    secondary: '#4a3e30',
    accent: '#a08860',
    detail: '#5a4e38',
    footer: '#6a5e48',
    lineArt: '#b8a888',
    lineArt2: '#a08860',
    isDark: false,
  },
  honey: {
    name: 'Honey',
    bg: '#faf4e6',
    primary: '#2a2010',
    secondary: '#4a3818',
    accent: '#e8a820',
    detail: '#5a4820',
    footer: '#6a5830',
    lineArt: '#c0a860',
    lineArt2: '#e8a820',
    isDark: false,
  },
  frost: {
    name: 'Frost',
    bg: '#f0f4f8',
    primary: '#181e28',
    secondary: '#303848',
    accent: '#4fc3f7',
    detail: '#404858',
    footer: '#506068',
    lineArt: '#90b0c8',
    lineArt2: '#4fc3f7',
    isDark: false,
  },
  coral: {
    name: 'Coral',
    bg: '#faf0ee',
    primary: '#2a1816',
    secondary: '#4a2828',
    accent: '#ff6f61',
    detail: '#5a3838',
    footer: '#6a4848',
    lineArt: '#d09088',
    lineArt2: '#ff6f61',
    isDark: false,
  },
  rose: {
    name: 'Rose',
    bg: '#faf0f2',
    primary: '#2a1820',
    secondary: '#4a2838',
    accent: '#e8708a',
    detail: '#5a3848',
    footer: '#6a4858',
    lineArt: '#c89098',
    lineArt2: '#e8708a',
    isDark: false,
  },
  ash: {
    name: 'Ash',
    bg: '#e8e6e4',
    primary: '#1a1a1c',
    secondary: '#3a3a40',
    accent: '#505058',
    detail: '#484850',
    footer: '#585860',
    lineArt: '#98989e',
    lineArt2: '#c08060',
    isDark: false,
  },
};

export function getScheme(name) {
  return SCHEMES[name] || SCHEMES.dark;
}

export function listSchemes() {
  return Object.entries(SCHEMES).map(([key, scheme]) => ({
    key,
    ...scheme,
  }));
}
